import { useCallback, useEffect, useRef, useState } from 'react';
import { IS_IOS_NON_SAFARI, IS_IOS_SAFARI } from './platform';

/**
 * 브라우저 내장 Web Speech API (SpeechRecognition) 래퍼.
 *
 * 지원 브라우저 (2026 기준):
 *  - Chrome 데스크탑/Android, Edge — 완전 지원, Chrome 139+ on-device 옵션 가능
 *  - Safari iOS 14.5+ — Apple 서버로 audio 전송 (권한 모달 표시)
 *  - **iOS Chrome / Firefox / Edge — 강제 미지원**: webkitSpeechRecognition 이 노출돼도
 *    WKWebView 권한 제약으로 즉시 service-not-allowed 에러를 던지는 경우가 많아 차라리
 *    서버 STT 폴백으로 자연스럽게 넘기는 게 UX 가 낫다.
 *  - Firefox 데스크탑 — 미구현 (Ctor null)
 */

// 표준 + webkit prefix 양쪽 처리.
function getRecognitionCtor(): {
  new (): SpeechRecognition;
} | null {
  if (typeof window === 'undefined') return null;
  // iOS 의 비-Safari 브라우저는 미지원 처리 (위 주석 참조).
  if (IS_IOS_NON_SAFARI) return null;
  const w = window as unknown as {
    SpeechRecognition?: { new (): SpeechRecognition };
    webkitSpeechRecognition?: { new (): SpeechRecognition };
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

// 브라우저별 prefix 차이 + 타입 정의 누락 회피용 최소 인터페이스.
interface SpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

export interface UseSpeechRecognitionOptions {
  lang?: string;
  /**
   * Web Speech API 의 continuous 플래그. 기본 true — 짧은 침묵에도 끊기지 않게 직접 silence 타이머로 관리.
   * false 면 첫 발화 단위 종료 (브라우저 기본 동작) 라 사용자가 잠깐 쉬면 끝나버린다.
   */
  continuous?: boolean;
  /** 발화 도중 임시 결과 노출. 기본 true. */
  interimResults?: boolean;
  /**
   * 마지막 발화 (interim 또는 final) 이후 이 시간만큼 침묵 지속되면 자동 종료. 기본 2000 (2초).
   * 0 이면 비활성 (사용자가 직접 stop 또는 maxDurationMs 만 의존).
   */
  silenceMs?: number;
  /** 안전 자동 종료 (ms). 기본 30000 (Gemma 4 audio 한도와 동일). 0 이면 비활성. */
  maxDurationMs?: number;
}

export interface UseSpeechRecognitionReturn {
  /** 브라우저가 SpeechRecognition 을 노출하는지. false 면 마이크 버튼 자체 숨길 것. */
  supported: boolean;
  listening: boolean;
  /** 최종 (isFinal=true) 으로 확정된 누적 텍스트. start() 호출 시 reset. */
  finalTranscript: string;
  /** 발화 도중 임시 텍스트 (회색 표시 등). 종료되면 빈 문자열. */
  interimTranscript: string;
  /** 마이크 권한 거부 / 네트워크 오류 / 인식 실패 등. */
  error: string | null;
  start(): void;
  stop(): void;
  reset(): void;
}

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {},
): UseSpeechRecognitionReturn {
  const {
    lang = 'ko-KR',
    continuous = true,
    interimResults = true,
    silenceMs = 2000,
    maxDurationMs = 30_000,
  } = options;

  const Ctor = getRecognitionCtor();
  const supported = Ctor !== null;

  const [listening, setListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * iOS Safari auto-restart 용 — onend 가 사용자/타이머가 의도해서 발생한 게 아니면
   * (즉, iOS 의 1-utterance 자동 종료) 같은 세션을 재시작.
   */
  const intentionalStopRef = useRef(false);
  /** 세션 시작 시각 — maxDurationMs 누적 체크용 (iOS 재시작 누적). */
  const sessionStartedAtRef = useRef<number>(0);

  const clearSafetyTimer = () => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  };

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const stop = useCallback(() => {
    intentionalStopRef.current = true;
    clearSafetyTimer();
    clearSilenceTimer();
    const r = recognitionRef.current;
    if (r) {
      try {
        r.stop();
      } catch {
        // 이미 종료된 상태면 무시.
      }
    }
  }, []);

  const reset = useCallback(() => {
    setFinalTranscript('');
    setInterimTranscript('');
    setError(null);
  }, []);

  /**
   * 새 SpeechRecognition 인스턴스를 만들어 시작. start() (사용자) + iOS auto-restart 양쪽에서 호출.
   * 내부에서 transcript reset 은 안 함 (재시작 시 누적 보존). 호출자가 필요하면 명시 reset.
   */
  const launchRecognition = useCallback(() => {
    if (!Ctor) {
      setError('이 브라우저는 음성 인식을 지원하지 않습니다.');
      return;
    }
    // 이미 살아있는 인스턴스가 있으면 abort (보호적).
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        // ignore
      }
    }

    const r = new Ctor();
    r.lang = lang;
    r.continuous = continuous;
    r.interimResults = interimResults;
    r.maxAlternatives = 1;

    const armSilenceTimer = () => {
      if (silenceMs <= 0) return;
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        intentionalStopRef.current = true; // silence 로 끝났음 — iOS 재시작 안 함.
        try {
          r.stop();
        } catch {
          // ignore
        }
      }, silenceMs);
    };

    r.onstart = () => {
      setListening(true);
      // 안전 타이머 — 누적 시간 (재시작 포함) 으로 계산.
      if (maxDurationMs > 0) {
        clearSafetyTimer();
        const remaining = maxDurationMs - (Date.now() - sessionStartedAtRef.current);
        if (remaining <= 0) {
          intentionalStopRef.current = true;
          try {
            r.stop();
          } catch {
            // ignore
          }
        } else {
          safetyTimerRef.current = setTimeout(() => {
            intentionalStopRef.current = true;
            try {
              r.stop();
            } catch {
              // ignore
            }
          }, remaining);
        }
      }
      armSilenceTimer();
    };

    r.onresult = (e: SpeechRecognitionEvent) => {
      let interim = '';
      let appendedFinal = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const result = e.results[i];
        if (!result) continue;
        const alt = result[0];
        if (!alt) continue;
        if (result.isFinal) {
          appendedFinal += alt.transcript;
        } else {
          interim += alt.transcript;
        }
      }
      setInterimTranscript(interim);
      if (appendedFinal) {
        setFinalTranscript((prev) => (prev ? `${prev} ${appendedFinal}` : appendedFinal));
      }
      // 발화 (interim 또는 final) 가 들어왔으니 silence 카운트 리셋.
      armSilenceTimer();
    };

    r.onerror = (e: SpeechRecognitionErrorEvent) => {
      const code = e.error;
      // no-speech / aborted 는 silence/stop 타이머의 정상 흐름과 겹치므로 에러로 노출하지 않음.
      if (code === 'no-speech' || code === 'aborted') return;
      // 진짜 에러 — iOS 재시작 안 함.
      intentionalStopRef.current = true;
      const msg =
        code === 'not-allowed' || code === 'service-not-allowed'
          ? '마이크 권한이 거부되었습니다.'
          : code === 'audio-capture'
            ? '마이크에 접근할 수 없습니다.'
            : code === 'network'
              ? '음성 인식 서비스에 연결하지 못했습니다.'
              : `음성 인식 오류 (${code}).`;
      setError(msg);
    };

    r.onend = () => {
      clearSafetyTimer();
      clearSilenceTimer();
      recognitionRef.current = null;
      // iOS Safari workaround — `continuous: true` 가 무시되어 첫 발화 후 자동 종료됨.
      // 사용자/silence/maxDuration 으로 의도된 종료가 아니면 같은 세션 이어서 재시작.
      if (
        IS_IOS_SAFARI &&
        !intentionalStopRef.current &&
        maxDurationMs > 0 &&
        Date.now() - sessionStartedAtRef.current < maxDurationMs
      ) {
        // 약간의 delay 후 재시작 — onend 동기 안에서 r.start() 하면 일부 브라우저가 거부.
        setTimeout(() => {
          if (!intentionalStopRef.current) launchRecognition();
        }, 150);
        return;
      }
      // 정상 종료.
      setListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = r;
    try {
      r.start();
    } catch (err) {
      setError(err instanceof Error ? err.message : '시작에 실패했습니다.');
      recognitionRef.current = null;
      setListening(false);
    }
  }, [Ctor, lang, continuous, interimResults, silenceMs, maxDurationMs]);

  const start = useCallback(() => {
    intentionalStopRef.current = false;
    sessionStartedAtRef.current = Date.now();
    setError(null);
    setFinalTranscript('');
    setInterimTranscript('');
    launchRecognition();
  }, [launchRecognition]);

  // unmount 시 정리.
  useEffect(() => {
    return () => {
      clearSafetyTimer();
      clearSilenceTimer();
      const r = recognitionRef.current;
      if (r) {
        try {
          r.abort();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  return {
    supported,
    listening,
    finalTranscript,
    interimTranscript,
    error,
    start,
    stop,
    reset,
  };
}
